import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Tag as TagIcon, ShoppingBag, Sparkles, Wrench } from "lucide-react";
import { useCart } from "@/lib/cart";
import { whatsappLinks } from "@/lib/whatsapp";

// Mobile-only floating action bar (hidden ≥ md).
// 5 actions: Anrufen · WhatsApp · [Reparatur center-hero] · Shop · AI Assistent.
// Auto-hides on scroll down, re-appears on scroll up. Hidden on /checkout routes.
export default function MobileActionBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { count, setOpen: setCartOpen } = useCart();
  const wa = whatsappLinks.generic();

  const [visible, setVisible] = useState(true);
  const [lastY, setLastY] = useState(0);

  // Auto-hide on scroll down
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      if (y < 80) {
        setVisible(true);
      } else if (y > lastY + 6) {
        setVisible(false); // scrolling down → hide
      } else if (y < lastY - 6) {
        setVisible(true); // scrolling up → show
      }
      setLastY(y);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [lastY]);

  // Hide bar on checkout flow so it doesn't compete with the pay CTA
  if (location.pathname.startsWith("/checkout") || location.pathname.startsWith("/admin")) return null;

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  const openAiChat = () => window.dispatchEvent(new CustomEvent("hdh:open-ai-chat"));
  const openCart = () => setCartOpen(true);
  const goRepair = () => {
    if (location.pathname === "/") {
      const el = document.getElementById("configurator");
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
    }
    navigate("/reparaturen");
  };

  return (
    <div
      className={`md:hidden fixed left-0 right-0 z-40 pointer-events-none transition-all duration-300 ${
        visible ? "translate-y-0 opacity-100" : "translate-y-[120%] opacity-0"
      }`}
      style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)" }}
      data-testid="mobile-action-bar"
    >
      <div className="px-4">
        <div className="pointer-events-auto mx-auto max-w-md relative">
          {/* Glow underneath */}
          <div className="absolute inset-x-8 -bottom-1 h-10 bg-[#DC1928]/25 blur-2xl rounded-full"></div>

          {/* Pill */}
          <div className="relative bg-[#101013]/85 backdrop-blur-xl border border-white/10 rounded-[28px] shadow-[0_20px_50px_-12px_rgba(0,0,0,0.8)] grid grid-cols-5 items-center gap-1 px-2.5 pt-2 pb-2.5">
            {/* 1. Verkaufen — KI-Ankaufsstelle */}
            <BarLink
              as={Link}
              to="/ankauf"
              icon={TagIcon}
              label="Verkaufen"
              testid="mab-buyback"
              accentColor="#FF2D40"
              active={isActive("/ankauf")}
            />

            {/* 2. WhatsApp */}
            <BarLink
              as="a"
              href={wa.href}
              onClick={wa.onClick}
              target="_blank"
              rel="noreferrer"
              icon={WhatsAppIcon}
              label="WhatsApp"
              testid="mab-whatsapp"
              accentColor="#25D366"
            />

            {/* 3. Reparatur — Hero center button (raised) */}
            <HeroCenter onClick={goRepair} testid="mab-repair" />

            {/* 4. Shop with cart badge */}
            <BarLink
              as="button"
              onClick={openCart}
              icon={ShoppingBag}
              label="Shop"
              testid="mab-shop"
              active={isActive("/zubehoer")}
              badge={count > 0 ? count : null}
            />

            {/* 5. AI Assistent */}
            <BarLink
              as="button"
              onClick={openAiChat}
              icon={Sparkles}
              label="AI"
              testid="mab-ai"
              accentColor="#FF2D40"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- Pill items ---------- */

function BarLink({ as = "button", icon: Icon, label, testid, active, badge, accentColor, ...rest }) {
  const Tag = as;
  return (
    <Tag
      {...rest}
      className="relative flex flex-col items-center justify-center gap-0.5 py-1.5 rounded-2xl tap-highlight active:scale-95 transition-transform"
      data-testid={testid}
    >
      <div
        className={`relative w-9 h-9 rounded-xl flex items-center justify-center transition-colors ${
          active ? "bg-[#DC1928]/15" : "bg-transparent"
        }`}
      >
        <Icon
          className={`w-[19px] h-[19px] ${
            active ? "text-[#FF2D40]" : "text-zinc-300"
          }`}
          style={accentColor && !active ? { color: accentColor } : undefined}
        />
        {badge ? (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#DC1928] text-white text-[10px] font-bold flex items-center justify-center border-2 border-[#101013]">
            {badge}
          </span>
        ) : null}
      </div>
      <span
        className={`text-[10px] tracking-wide font-medium ${
          active ? "text-white" : "text-zinc-500"
        }`}
      >
        {label}
      </span>
    </Tag>
  );
}

function HeroCenter({ onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="relative flex flex-col items-center -mt-7 tap-highlight active:scale-95 transition-transform"
      aria-label="Reparatur anfragen"
    >
      {/* Glow halo */}
      <span className="absolute inset-0 -m-1 rounded-full bg-[#DC1928]/40 blur-xl"></span>
      <span className="relative w-[58px] h-[58px] rounded-full bg-gradient-to-br from-[#FF2D40] to-[#B01829] border-4 border-[#101013] flex items-center justify-center shadow-[0_10px_30px_-6px_rgba(220,25,40,0.7)]">
        <Wrench className="w-6 h-6 text-white" strokeWidth={2.3} />
      </span>
      <span className="relative mt-1 text-[10px] tracking-wide font-semibold text-white">
        Reparatur
      </span>
    </button>
  );
}

function WhatsAppIcon({ className, ...rest }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" {...rest}>
      <path d="M17.498 14.382c-.301-.15-1.767-.872-2.04-.972-.273-.1-.472-.15-.672.15-.197.298-.771.97-.944 1.17-.174.2-.347.226-.646.075-.3-.15-1.263-.465-2.403-1.485-.888-.795-1.484-1.77-1.66-2.07-.174-.3-.019-.465.13-.615.136-.135.301-.345.451-.523.146-.181.194-.301.297-.501.099-.21.049-.375-.025-.524-.075-.15-.672-1.62-.922-2.206-.24-.584-.487-.51-.672-.51-.172-.015-.371-.015-.571-.015-.2 0-.523.074-.797.359-.273.3-1.045 1.02-1.045 2.475s1.07 2.865 1.219 3.075c.149.18 2.105 3.195 5.1 4.485.714.3 1.27.48 1.704.629.714.227 1.365.195 1.88.121.574-.091 1.767-.721 2.016-1.426.255-.705.255-1.29.18-1.425-.074-.135-.27-.225-.57-.345m-5.446 7.443h-.016a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}
